import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { C, alpha } from '../lib/theme.js';
import { apiFetch } from '../lib/api.js';
import { useOrg } from './OrgContext.js';
import { useAuth } from '../auth/AuthProvider.js';
import type { BotActionKey, BotNavigate, BotPrefill, BotReply } from '../lib/types.js';
import botIcon from '../assets/basil-bot.png';

// Handoff key the Launch screen reads once on mount to pre-fill a boost (keep in
// sync with PREFILL_KEY in pages/Launch.tsx).
const LAUNCH_PREFILL_KEY = 'basil:launch:prefill';

interface Msg {
  role: 'user' | 'assistant';
  content: string;
  navigate?: BotNavigate | null;
  logRequest?: { title: string; detail: string } | null;
  logged?: boolean;
  action?: BotActionKey | null;
  actionResult?: string;
  actionBusy?: boolean;
  prefill?: BotPrefill | null;
  hidden?: boolean; // continuation turns sent after an action — kept in history, not shown
}

// Safe one-click actions the bot can run (no spend). Maps a marker → endpoint.
const BOT_ACTIONS: Record<
  BotActionKey,
  { label: string; path: (o: string) => string; done: (d: Record<string, unknown>) => string }
> = {
  sync_cloudbeds: {
    label: 'Sync Cloudbeds now',
    path: (o) => `/api/orgs/${o}/cloudbeds/sync`,
    done: (d) => `✓ Synced ${Number(d.written ?? 0)} reservations.`,
  },
  sync_meta: {
    label: 'Sync Meta now',
    path: (o) => `/api/orgs/${o}/meta/sync`,
    done: (d) => `✓ Synced Meta (${Number(d.written ?? 0)} rows).`,
  },
  capi_push: {
    label: 'Push bookings to Meta now',
    path: (o) => `/api/orgs/${o}/meta/capi/push`,
    done: (d) => (d.reason ? `${d.reason}` : `✓ Pushed ${Number(d.sent ?? 0)} bookings to Meta.`),
  },
};

// Chat history persists in the browser, keyed per user, so it survives navigation
// and reloads and you can scroll back through it.
const chatKey = (uid: string) => `basil:chat:${uid}`;
function loadChat(uid: string): Msg[] {
  try {
    const raw = localStorage.getItem(chatKey(uid));
    const parsed = raw ? (JSON.parse(raw) as Msg[]) : null;
    return Array.isArray(parsed) && parsed.length ? parsed : [GREETING];
  } catch {
    return [GREETING];
  }
}
function saveChat(uid: string, msgs: Msg[]): void {
  try {
    localStorage.setItem(chatKey(uid), JSON.stringify(msgs));
  } catch {
    /* storage unavailable — ignore */
  }
}

/** Render **bold** markdown as real bold (subjects / UI names) — no heavy dep. */
function renderRich(text: string) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
    const m = part.match(/^\*\*([^*]+)\*\*$/);
    return m ? <strong key={i}>{m[1]}</strong> : <span key={i}>{part}</span>;
  });
}

const GREETING: Msg = {
  role: 'assistant',
  content:
    "Hi, I'm Basil. Ask me how to do anything — like “how do I make a carousel?” or “how do I launch an ad?” and I'll walk you through it (and take you there).",
};

/** Basil Bot — floating in-app help assistant (Phase 1: answers + jump-to-page). */
export function BasilBot() {
  const { orgId } = useOrg();
  const { session } = useAuth();
  const userId = session?.user?.id ?? 'anon';
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>(() => loadChat(userId));
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Always-current view of msgs, so callbacks (action continuation) read the latest.
  const msgsRef = useRef(msgs);
  msgsRef.current = msgs;

  // Persist history per user (survives navigation + reload).
  useEffect(() => {
    saveChat(userId, msgs);
  }, [msgs, userId]);
  const clearChat = () => setMsgs([GREETING]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [msgs, open]);

  // Core: given the full history (already including the new user turn), post to
  // the assistant and append its reply. Shared by ask() and action continuation.
  async function respond(history: Msg[]) {
    if (!orgId) return;
    setMsgs(history);
    setBusy(true);
    try {
      // The API must start with a user turn, so drop any leading greeting/assistant.
      const hist = history.map((m) => ({ role: m.role, content: m.content }));
      const start = hist.findIndex((m) => m.role === 'user');
      const messages = start >= 0 ? hist.slice(start) : hist;
      const d = await apiFetch<BotReply>(`/api/orgs/${orgId}/assistant`, {
        method: 'POST',
        body: JSON.stringify({ messages }),
      });
      setMsgs((m) => [
        ...m,
        {
          role: 'assistant',
          content: d.reply,
          navigate: d.navigate,
          logRequest: d.logRequest,
          action: d.action ?? null,
          prefill: d.prefill ?? null,
        },
      ]);
    } catch (e) {
      setMsgs((m) => [
        ...m,
        {
          role: 'assistant',
          content: e instanceof Error ? `Sorry — ${e.message}` : 'Something went wrong.',
        },
      ]);
    } finally {
      setBusy(false);
    }
  }
  async function ask(text: string) {
    const t = text.trim();
    if (!t || busy || !orgId) return;
    await respond([...msgsRef.current, { role: 'user', content: t }]);
  }
  function send() {
    const t = input.trim();
    if (!t) return;
    setInput('');
    void ask(t);
  }

  // Let other screens (e.g. the Home ask box) hand a question to Basil: it opens
  // the panel with the full history and answers, resuming the same conversation.
  const askRef = useRef<(t: string) => void>(() => {});
  askRef.current = (t: string) => void ask(t);
  useEffect(() => {
    const h = (e: Event) => {
      setOpen(true);
      const detail = (e as CustomEvent<string>).detail;
      if (typeof detail === 'string' && detail.trim()) askRef.current(detail);
    };
    window.addEventListener('basil:ask', h as EventListener);
    return () => window.removeEventListener('basil:ask', h as EventListener);
  }, []);

  async function logIt(idx: number, req: { title: string; detail: string }) {
    if (!orgId) return;
    try {
      await apiFetch(`/api/orgs/${orgId}/feature-requests`, {
        method: 'POST',
        body: JSON.stringify({ title: req.title, detail: req.detail, source: 'basil-bot' }),
      });
      setMsgs((m) => m.map((x, i) => (i === idx ? { ...x, logged: true } : x)));
    } catch (e) {
      setMsgs((m) => [
        ...m,
        {
          role: 'assistant',
          content: e instanceof Error ? `Couldn't log that — ${e.message}` : 'Failed to log.',
        },
      ]);
    }
  }

  // Run a safe, one-click action (sync / push — never spend). Human-confirmed.
  async function runAction(idx: number, key: BotActionKey) {
    if (!orgId) return;
    const spec = BOT_ACTIONS[key];
    setMsgs((m) => m.map((x, i) => (i === idx ? { ...x, actionBusy: true } : x)));
    try {
      const d = await apiFetch<Record<string, unknown>>(spec.path(orgId), { method: 'POST' });
      const result = spec.done(d ?? {});
      const done = msgsRef.current.map((x, i) =>
        i === idx ? { ...x, actionBusy: false, actionResult: result } : x,
      );
      setMsgs(done);
      // Hand the fresh result back so Basil finishes the answer it promised
      // (e.g. "let me pull Meta…" → now actually judge the post). Hidden turn.
      await respond([
        ...done,
        {
          role: 'user',
          hidden: true,
          content: `[Done: ${spec.label} — ${result}] Now continue and answer my previous question using this fresh data. Do not offer that same action again.`,
        },
      ]);
    } catch (e) {
      setMsgs((m) =>
        m.map((x, i) =>
          i === idx
            ? {
                ...x,
                actionBusy: false,
                actionResult: e instanceof Error ? `Couldn't do that — ${e.message}` : 'Failed.',
              }
            : x,
        ),
      );
    }
  }

  // Hand a pre-designed boost to the Launch screen and go there. Nothing spends:
  // the user still reviews, clicks Build (paused), then Go Live.
  function applyPrefill(pf: BotPrefill) {
    try {
      localStorage.setItem(LAUNCH_PREFILL_KEY, JSON.stringify(pf));
    } catch {
      /* storage unavailable — ignore, they can still fill it in manually */
    }
    setOpen(false);
    navigate('/launch');
  }

  if (!open) {
    return (
      <div
        onClick={() => setOpen(true)}
        title="Ask Basil"
        style={{
          position: 'fixed',
          bottom: 22,
          right: 22,
          zIndex: 200,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 6,
          cursor: 'pointer',
        }}
      >
        <span
          style={{
            padding: '3px 10px',
            borderRadius: 999,
            background: C.surface,
            border: `1px solid ${C.line}`,
            color: C.primary,
            fontSize: 11.5,
            fontWeight: 600,
            boxShadow: '0 4px 14px rgba(0,0,0,.3)',
          }}
        >
          Basil
        </span>
        <button
          type="button"
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            border: `2px solid ${C.basil}`,
            padding: 0,
            overflow: 'hidden',
            cursor: 'pointer',
            background: '#fff',
            boxShadow: '0 10px 30px rgba(0,0,0,.45)',
          }}
        >
          <img
            src={botIcon}
            alt="Basil"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 22,
        right: 22,
        width: 'min(380px, calc(100vw - 32px))',
        height: 'min(560px, calc(100vh - 90px))',
        display: 'flex',
        flexDirection: 'column',
        background: C.surface,
        border: `1px solid ${C.line}`,
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 24px 64px rgba(0,0,0,.55)',
        zIndex: 200,
        animation: 'basilfade .2s ease',
      }}
    >
      {/* header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '13px 15px',
          borderBottom: `1px solid ${C.lineSoft}`,
          background: C.base,
        }}
      >
        <img
          src={botIcon}
          alt="Basil"
          style={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            objectFit: 'cover',
            background: '#fff',
            border: `1.5px solid ${alpha(C.basil, 50)}`,
          }}
        />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: C.primary }}>Basil</div>
          <div style={{ font: `500 9px 'IBM Plex Mono',monospace`, color: C.faint }}>
            IN-APP ASSISTANT
          </div>
        </div>
        {msgs.length > 1 && (
          <span
            onClick={clearChat}
            style={{
              cursor: 'pointer',
              color: C.muted,
              font: `500 10px 'IBM Plex Mono',monospace`,
              letterSpacing: '.04em',
            }}
            title="Clear chat history"
          >
            CLEAR
          </span>
        )}
        <span
          onClick={() => setOpen(false)}
          style={{ cursor: 'pointer', color: C.muted, fontSize: 18, lineHeight: 1 }}
          title="Close"
        >
          ✕
        </span>
      </div>

      {/* messages */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 14,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {msgs.map((m, i) =>
          m.hidden ? null : (
            <div
              key={i}
              style={{
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '86%',
              }}
            >
              <div
                style={{
                  padding: '9px 12px',
                  borderRadius: 12,
                  fontSize: 13,
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  background: m.role === 'user' ? C.basil : C.surface2,
                  color: m.role === 'user' ? C.basilInk : C.primary,
                  border: m.role === 'user' ? 'none' : `1px solid ${C.line}`,
                }}
              >
                {renderRich(m.content)}
              </div>
              {m.navigate && (
                <button
                  type="button"
                  onClick={() => {
                    navigate(m.navigate!.route);
                    setOpen(false);
                  }}
                  style={{
                    marginTop: 6,
                    padding: '6px 11px',
                    borderRadius: 8,
                    border: `1px solid ${alpha(C.basil, 45)}`,
                    background: alpha(C.basil, 12),
                    color: C.basil,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  Take me to {m.navigate.label} →
                </button>
              )}
              {m.logRequest &&
                (m.logged ? (
                  <div style={{ marginTop: 6, fontSize: 12, color: C.good }}>
                    ✓ Logged for the team to review.
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => void logIt(i, m.logRequest!)}
                    style={{
                      marginTop: 6,
                      padding: '6px 11px',
                      borderRadius: 8,
                      border: `1px solid ${alpha(C.basil, 45)}`,
                      background: alpha(C.basil, 12),
                      color: C.basil,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    ＋ Log this as a to-do
                  </button>
                ))}
              {m.action &&
                (m.actionResult ? (
                  <div style={{ marginTop: 6, fontSize: 12, color: C.good }}>{m.actionResult}</div>
                ) : (
                  <button
                    type="button"
                    disabled={m.actionBusy}
                    onClick={() => void runAction(i, m.action!)}
                    style={{
                      marginTop: 6,
                      padding: '6px 11px',
                      borderRadius: 8,
                      border: `1px solid ${alpha(C.basil, 45)}`,
                      background: C.basil,
                      color: C.basilInk,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: m.actionBusy ? 'default' : 'pointer',
                      opacity: m.actionBusy ? 0.5 : 1,
                      fontFamily: 'inherit',
                    }}
                  >
                    {m.actionBusy ? 'Working…' : BOT_ACTIONS[m.action].label}
                  </button>
                ))}
              {m.prefill && (
                <button
                  type="button"
                  onClick={() => applyPrefill(m.prefill!)}
                  style={{
                    marginTop: 6,
                    padding: '6px 11px',
                    borderRadius: 8,
                    border: 'none',
                    background: C.basil,
                    color: C.basilInk,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  Set it up in the Boost tool →
                </button>
              )}
            </div>
          ),
        )}
        {busy && (
          <div
            style={{ alignSelf: 'flex-start', color: C.muted, fontSize: 13, padding: '4px 6px' }}
          >
            Basil is typing…
          </div>
        )}
      </div>

      {/* input */}
      <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: `1px solid ${C.lineSoft}` }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void send();
            }
          }}
          placeholder="Ask how to do something…"
          autoFocus
          style={{
            flex: 1,
            minWidth: 0,
            background: C.surface2,
            border: `1px solid ${C.line}`,
            borderRadius: 10,
            padding: '10px 12px',
            fontSize: 13,
            color: C.primary,
            fontFamily: 'inherit',
          }}
        />
        <button
          type="button"
          onClick={() => void send()}
          disabled={busy || !input.trim()}
          style={{
            padding: '0 14px',
            borderRadius: 10,
            border: 'none',
            background: C.basil,
            color: C.basilInk,
            fontSize: 13,
            fontWeight: 600,
            cursor: busy || !input.trim() ? 'default' : 'pointer',
            opacity: busy || !input.trim() ? 0.5 : 1,
            fontFamily: 'inherit',
          }}
        >
          Send
        </button>
      </div>
    </div>
  );
}
